import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { PasswordInput } from '../../components/ui/password-input'
import { Card } from '../../components/ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { TOTPVerify } from '../../components/auth/totp-verify'
import { processAuthLink, cleanupAuthLinkUrl, getRedirectUrlBasedOnRole } from '../../lib/auth-link-utils'
import { getUserProfile } from '../../lib/api/api' // For user profile
import { env } from '../../lib/env' // For environment variables

export function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [hasAccessToken, setHasAccessToken] = useState(false)
  const [needsMfa, setNeedsMfa] = useState(false)
  const [pendingPassword, setPendingPassword] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()

  useEffect(() => {
    const handleAuthFlow = async () => {
      console.log('[ResetPasswordPage] useEffect - handleAuthFlow called');
      console.log('[ResetPasswordPage] Location details:', {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash
      });

      setIsLoading(true);

      try {
        // First check if we already have an active session
        console.log('[ResetPasswordPage] Checking for existing session first...');
        const { data: { session: existingSession } } = await supabase.auth.getSession();

        if (existingSession) {
          console.log('[ResetPasswordPage] Found existing active session');
          console.log('[ResetPasswordPage] Existing session details:', {
            userId: existingSession.user.id,
            email: existingSession.user.email,
            expiresAt: existingSession.expires_at
          });
          setHasAccessToken(true);
          setIsLoading(false);
          return;
        }

        console.log('[ResetPasswordPage] No existing session, processing auth link...');
        const result = await processAuthLink(supabase, location);

        console.log('[ResetPasswordPage] ProcessAuthLink result:', {
          success: result.success,
          hasSession: !!result.session,
          hasError: !!result.error,
          errorMessage: result.error?.message
        });

        if (result.success && result.session) {
          console.log('[ResetPasswordPage] Successfully authenticated via processAuthLink');
          setHasAccessToken(true);
          cleanupAuthLinkUrl(window.location, true); // Preserve hash for routing
        } else {
          console.log('[ResetPasswordPage] ProcessAuthLink failed or returned no session');

          // Check if we have any auth parameters that should have worked
          const searchParams = new URLSearchParams(location.search);
          const hasCode = searchParams.has('code');
          const hasTokensInHash = location.hash.includes('access_token=');

          console.log('[ResetPasswordPage] Auth parameter check:', {
            hasCode,
            hasTokensInHash,
            searchParams: location.search,
            hash: location.hash
          });

          if (hasCode || hasTokensInHash) {
            // We had auth parameters but they didn't work - this is an error
            console.error('[ResetPasswordPage] Auth parameters present but authentication failed');
            setHasAccessToken(false);

            const errorMessage = result.error?.message || 'Failed to process password reset link. The link may be expired or invalid.';

            toast({
              title: 'Authentication Failed',
              description: errorMessage,
              variant: 'destructive',
            });

            // Add delay before redirect to let user see the error
            setTimeout(() => {
              navigate('/auth/login', { replace: true });
            }, 3000);
          } else {
            // No auth parameters - user probably navigated directly to reset page
            console.log('[ResetPasswordPage] No auth parameters found - invalid access');
            setHasAccessToken(false);

            toast({
              title: 'Invalid Access',
              description: 'Please use the password reset link from your email to access this page.',
              variant: 'destructive',
            });

            navigate('/auth/forgot-password', { replace: true });
          }
        }
      } catch (error) {
        console.error('[ResetPasswordPage] Unexpected error in handleAuthFlow:', error);
        setHasAccessToken(false);

        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

        toast({
          title: 'Error',
          description: `Failed to process password reset: ${errorMessage}`,
          variant: 'destructive',
        });

        navigate('/auth/login', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    handleAuthFlow();
  }, [navigate, toast, location]) // Removed supabase from dependencies as it's stable

  const validatePassword = (password: string): string | null => {
    if (password.length < 12) {
      return 'Password must be at least 12 characters long'
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter'
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter'
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number'
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      return 'Password must contain at least one special character'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    // Validate password
    console.log('[ResetPasswordPage] handleSubmit called');

    console.log('[ResetPasswordPage] Validating password format');
    // Validate password
    const passwordError = validatePassword(password)
    if (passwordError) {
      console.log('[ResetPasswordPage] Password validation failed:', passwordError);
      setFormError(passwordError)
      return
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      console.log('[ResetPasswordPage] Passwords do not match.');
      setFormError('Passwords do not match')
      return
    }

    console.log('[ResetPasswordPage] Passwords match and validation passed. Setting isLoading to true.');
    setIsLoading(true)
    try {
      console.log('[ResetPasswordPage] Attempting supabase.auth.updateUser');
      const { error } = await supabase.auth.updateUser({ password })

      if (error) {
        console.error('[ResetPasswordPage] Error from supabase.auth.updateUser:', error);
        // Check if the error is due to MFA requirement
        if (error.message.includes('AAL2 session is required')) {
          console.log('[ResetPasswordPage] MFA is required. Setting needsMfa to true.');
          setPendingPassword(password)
          setNeedsMfa(true)
          return
        }
        throw error
      }

      console.log('[ResetPasswordPage] supabase.auth.updateUser successful.');
      // If we get here, password was updated successfully
      await handleSuccessfulPasswordReset()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password'
      console.error('[ResetPasswordPage] Catch block in handleSubmit:', errorMessage, error);
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSuccessfulPasswordReset = async () => {
    console.log('[ResetPasswordPage] handleSuccessfulPasswordReset called');
    try {
      // 1. Check user profile BEFORE signing out (requires active session)
      console.log('[ResetPasswordPage] Fetching user profile before sign out.');
      const userProfile = await getUserProfile();
      console.log('[ResetPasswordPage] User profile fetched successfully');

      // 2. Determine if this is an entity user
      const isEntityUser = userProfile?.userRole?.startsWith('entity_');
      console.log('[ResetPasswordPage] Is entity user:', isEntityUser);

      // 3. Store the redirect URL before signout
      let redirectUrl = '/auth/login'; // Default to admin login

      if (isEntityUser && env.VITE_USER_DASHBOARD_URL) {
        // Construct partner login URL
        redirectUrl = `${env.VITE_USER_DASHBOARD_URL}/auth/login`;
        console.log('[ResetPasswordPage] Entity user detected, will redirect to partner login');
      } else {
        console.log('[ResetPasswordPage] Non-entity user or no partner URL, will redirect to admin login');
      }

      // 4. Sign out the user
      console.log('[ResetPasswordPage] Signing out user.');
      await supabase.auth.signOut();

      // 5. Show success message
      toast({
        title: 'Success',
        description: 'Your password has been reset successfully. Please log in with your new password.',
      });

      // 6. Perform the redirect
      if (isEntityUser && env.VITE_USER_DASHBOARD_URL) {
        // For external URLs, use window.location
        console.log('[ResetPasswordPage] Redirecting to partner login via window.location.href');
        window.location.href = redirectUrl;
      } else {
        // For internal URLs, use the router's navigate
        console.log('[ResetPasswordPage] Redirecting to admin login via navigate');
        navigate(redirectUrl, { replace: true });
      }
    } catch (error) {
      // 7. Handle errors - fall back to admin login
      console.error('[ResetPasswordPage] Error during password reset redirect logic:', error);
      // Ensure user is signed out even on error
      try {
        await supabase.auth.signOut();
        console.log('[ResetPasswordPage] Signed out user after error in redirect logic.');
      } catch (signOutError) {
        console.error('[ResetPasswordPage] Error signing out user after error in redirect logic:', signOutError);
      }
      toast({
        title: 'Success',
        description: 'Your password has been reset successfully. Please log in with your new password.',
      });
      console.log('[ResetPasswordPage] Navigating to admin login as fallback after error.');
      navigate('/auth/login', { replace: true });
    }
  };

  const handleMfaComplete = async () => {
    console.log('[ResetPasswordPage] handleMfaComplete called');
    setIsLoading(true)
    try {
      console.log('[ResetPasswordPage] Attempting supabase.auth.updateUser after MFA verification');
      // Try updating password again after MFA verification
      const { error } = await supabase.auth.updateUser({ password: pendingPassword })
      if (error) {
        console.error('[ResetPasswordPage] Error from supabase.auth.updateUser in handleMfaComplete:', error);
        throw error
      }

      console.log('[ResetPasswordPage] supabase.auth.updateUser successful in handleMfaComplete.');
      // Reuse the updated handleSuccessfulPasswordReset logic
      await handleSuccessfulPasswordReset();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password'
      console.error('[ResetPasswordPage] Catch block in handleMfaComplete:', errorMessage, error);
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
      setNeedsMfa(false)
      setPendingPassword('')
    }
  }

  // Show loading spinner while processing auth link
  if (isLoading && !hasAccessToken && !needsMfa) {
    return (
      <div className={`flex min-h-screen items-center justify-center ${getThemeClass('gradients.primary')}`}>
        <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <h1 className={`text-xl font-semibold ${getThemeClass('components.text.heading')}`}>Processing Reset Link</h1>
            <p className="text-sm text-gray-600 text-center">
              Please wait while we verify your password reset link...
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={`flex min-h-screen items-center justify-center ${getThemeClass('gradients.primary')}`}>
      <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
        <h1 className={`mb-6 text-2xl font-bold ${getThemeClass('components.text.heading')}`}>Set New Password</h1>
        {!hasAccessToken ? (
          <Alert variant="destructive">
            <AlertDescription>Invalid or expired password reset link. Please request a new password reset.</AlertDescription>
          </Alert>
        ) : needsMfa ? (
          <div className="space-y-4">
            <Alert>
              <AlertDescription>
                Two-factor authentication is required to complete your password reset.
              </AlertDescription>
            </Alert>
            <TOTPVerify onComplete={handleMfaComplete} />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <PasswordInput
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className={getThemeClass('components.input.base')}
              />
            </div>
            <div>
              <PasswordInput
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
                className={getThemeClass('components.input.base')}
              />
            </div>
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            <div className="text-sm text-gray-600">
              Password must:
              <ul className="list-disc pl-5 mt-1">
                <li>Be at least 12 characters long</li>
                <li>Contain at least one uppercase letter</li>
                <li>Contain at least one lowercase letter</li>
                <li>Contain at least one number</li>
                <li>Contain at least one special character</li>
              </ul>
            </div>
            <Button
              type="submit"
              className={`w-full ${getThemeClass('components.button.primary')}`}
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  )
}
